import React, { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';

import Section from '@rover/kibble/patterns/Section';
import Accordion, { AccordionItem } from '@rover/react-lib/src/components/lists/Accordion';
import FactCitations, {
  Citation,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import FooterLinkList from '@rover/react-lib/src/pages/shared/FooterLinks/FooterLinkList';
import { ApiFrontendInternalLinkingCityRetrieve200ResultsMetadataLocationType as LocationType } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingCityRetrieve200ResultsMetadataLocationType';

type LinkItem = {
  href: string;
  title: string;
};

type Props = {
  location: string;
  locationType?: LocationType;
  cityOrNeighborhoodServices: LinkItem[];
  citiesOrNeighborhoods: LinkItem[];
  citations?: Citation[];
};

const InternalLinkingFooter = ({
  location,
  locationType,
  cityOrNeighborhoodServices,
  citiesOrNeighborhoods,
  citations,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const serviceLinks = useMemo(
    () =>
      cityOrNeighborhoodServices.map((item) => ({
        name: item.title,
        url: item.href,
      })),
    [cityOrNeighborhoodServices]
  );

  const locationLinks = useMemo(
    () =>
      citiesOrNeighborhoods.map((item) => ({
        name: item.title,
        url: item.href,
      })),
    [citiesOrNeighborhoods]
  );

  const locationSectionTitle =
    locationType === LocationType.REGION
      ? t`Find Rover in your city`
      : t`Find Rover in your neighborhood`;

  return (
    <Section background="contrast" py="12x">
      <Accordion itemColorVariant="primary_inverse" headingSize="200">
        {serviceLinks.length > 0 && (
          <AccordionItem
            itemHeading={t`Top Pet Services in your location: ${location}`}
            itemBody={<FooterLinkList linkList={serviceLinks} numColumns={[1, 2, 4]} />}
            testID="footer-accordion-service-links"
          />
        )}

        {locationLinks.length > 0 && (
          <AccordionItem
            itemHeading={locationSectionTitle}
            itemBody={<FooterLinkList linkList={locationLinks} numColumns={[2, 6]} />}
            testID="footer-accordion-location-links"
          />
        )}

        {citations && <FactCitations citations={citations} />}
      </Accordion>
    </Section>
  );
};

export default InternalLinkingFooter;
